import { reactive } from "vue";
import { verifyAddress as verifyAddressRequest } from "../api/addresses";

export function useVerifyAddress() {
    const verificationErrors: Record<string, string> = reactive({
        line1: "",
        line2: "",
        postal_code: "",
    });

    /**
     * Verify the address, then run the callback from the request's `.finally()` — once the
     * response has landed, whether it verified or not. An address too incomplete to verify skips
     * the request and runs the callback immediately.
     *
     * Callers depend on that ordering rather than merely benefiting from it: the checkout
     * shipping form issues its tax quote from this callback, and the quote reads a standardized
     * address that only this request caches. Running the callback before the response would
     * silently revert US quotes to the typed address.
     */
    function verifyAddress(inputs: any, callback?: () => void) {
        if (
            (inputs.line1 && inputs.city && inputs.state) ||
            (inputs.line1 && inputs.city && inputs.postal_code)
        ) {
            setTimeout(() => {
                verifyAddressRequest(inputs)
                    .then(() => {
                        for (const field in verificationErrors) {
                            if (
                                Object.prototype.hasOwnProperty.call(
                                    verificationErrors,
                                    field,
                                )
                            ) {
                                verificationErrors[field] = "";
                            }
                        }
                    })
                    .catch((error) => {
                        if (
                            typeof error.response !== "undefined" &&
                            Object.prototype.hasOwnProperty.call(
                                error.response,
                                "data",
                            ) &&
                            Object.prototype.hasOwnProperty.call(
                                error.response.data,
                                "errors",
                            )
                        ) {
                            for (const field in verificationErrors) {
                                if (
                                    Object.prototype.hasOwnProperty.call(
                                        verificationErrors,
                                        field,
                                    )
                                ) {
                                    verificationErrors[field] =
                                        Object.prototype.hasOwnProperty.call(
                                            error.response.data.errors,
                                            field,
                                        ) &&
                                        typeof error.response.data.errors[
                                            field
                                        ] === "string"
                                            ? error.response.data.errors[field]
                                            : "";
                                }
                            }
                        }
                    })
                    .finally(() => {
                        if (callback) {
                            callback();
                        }
                    });
            }, 500);
        } else {
            if (callback) {
                callback();
            }
        }
    }

    return {
        verificationErrors,
        verifyAddress,
    };
}
